import { useMemo } from 'react';

import { useCacheQuery } from '../../../hooks/use-cache-query';
import {
  GetRegistrationItemDocument,
  RegistrationDetailItemDataFragment,
} from '../../types/api/graphql';

/**
 * One registration — the household plus every person on it.
 *
 * Deliberately `network-only`: the server decrypts `bsn` only while the
 * request is Open and logs every read of this query as the BSN access trail.
 * Answering from the cache would both skip that trail entry and risk showing
 * a BSN the server would no longer hand out.
 */
export function useRegistrationItem(registrationId: string | null | undefined) {
  const {
    data: registrationItemData,
    loading: registrationItemLoading,
    refetch,
  } = useCacheQuery(GetRegistrationItemDocument, {
    variables: {
      registrationId: registrationId ?? '',
    },
    skip: !registrationId,
    fetchPolicy: 'network-only',
  });

  const data: RegistrationDetailItemDataFragment | undefined = useMemo(
    () =>
      (registrationItemData?.registration as RegistrationDetailItemDataFragment) ??
      undefined,
    [registrationItemData?.registration]
  );

  return {
    data,
    loading: registrationItemLoading,
    refetch,
  };
}
